import { getDataSource } from '@/lib/data-source';

export interface Product {
  id: string;
  name: string;
  description: string;
  visibility?: 'public' | 'private';
  name_translations?: { [locale: string]: string };
  description_translations?: { [locale: string]: string };
  supported_languages?: string[];
  status: 'draft' | 'published' | 'archived' | 'suspended' | 'active';
  status_label?: string;
  status_color?: string;
  version: string;
  endpoint_count: number;
  customer_count?: number;
  health_score?: number;
  
  // New usage statistics structure from JSON:API
  usage_statistics?: {
    total_requests: number;
    requests_today: number;
    requests_this_month: number;
    active_customers: number;
    avg_requests_per_customer: number;
    daily_growth_rate: number;
  };
  
  // Legacy usage stats for backward compatibility
  usage_stats?: {
    total_calls: number;
    last_24h: number;
    active_customers: number;
  };
  
  endpoint_summary?: {
    total: number;
    by_method: Record<string, unknown>[];
    most_used_method: string | null;
  };
  
  configuration?: {
    authentication_type: string;
    authentication_label: string;
    rate_limits: {
      requests_per_minute: number;
      requests_per_hour: number;
      requests_per_day: number;
    };
  };
  
  tags: string[];
  rate_limits?: {
    requests_per_minute?: number;
    requests_per_hour?: number;
    requests_per_day?: number;
  };
  authentication_type?: 'api_key' | 'oauth2' | 'jwt' | 'custom';
  
  // Additional JSON:API fields
  translations?: {
    name?: { [locale: string]: string };
    description?: { [locale: string]: string };
  };
  is_multi_language?: boolean;
  version_history?: Array<{
    version: string;
    created_at: string;
    is_current: boolean;
    changes: string | null;
  }>;
  is_active?: boolean;
  is_published?: boolean;
  has_endpoints?: boolean;
  has_customers?: boolean;
  has_usage?: boolean;
  
  created_at: string;
  updated_at: string;
}

export interface ProductListParams {
  search?: string;
  status?: 'draft' | 'published' | 'archived' | 'suspended' | 'active';
  tags?: string[];
  visibility?: 'public' | 'private';
  page?: number;
  per_page?: number;
}

export interface ProductCreateData {
  name: string;
  description: string;
  visibility?: 'public' | 'private';
  name_translations?: { [locale: string]: string };
  description_translations?: { [locale: string]: string };
  supported_languages?: string[];
  version: string;
  status?: 'draft' | 'published' | 'archived' | 'suspended' | 'active';
  tags?: string[];
  rate_limits?: {
    requests_per_minute?: number;
    requests_per_hour?: number;
    requests_per_day?: number;
  };
  authentication_type?: 'api_key' | 'oauth2' | 'jwt' | 'custom';
}

export interface ProductUpdateData extends Partial<ProductCreateData> {
  name_translations?: { [locale: string]: string };
  description_translations?: { [locale: string]: string };
  supported_languages?: string[];
  authentication?: {
    supported_types: string[];
    required: boolean;
  };
}

export interface ProductListResponse {
  data: Product[];
  total: number;
  page: number;
  per_page: number;
}

class ProductsService {
  private dataSource = getDataSource();

  async getProducts(params?: ProductListParams): Promise<ProductListResponse> {
    try {
      const response = await this.dataSource.get<ProductListResponse>('/api/v1/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async getProduct(id: string): Promise<Product> {
    try {
      const response = await this.dataSource.get<Product>(`/api/v1/products/${id}`);
      console.log('Product service response:', response);
      console.log('Product data:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching product ${id}:`, error);
      throw error;
    }
  }

  async createProduct(data: ProductCreateData): Promise<Product> {
    try {
      const response = await this.dataSource.post<Product>('/api/v1/products', {
        ...data,
        visibility: data.visibility ?? 'public',
      });
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  async updateProduct(id: string, data: ProductUpdateData): Promise<Product> {
    try {
      // Transform frontend data to backend format
      const backendData = { ...data };
      
      // Convert authentication_type back to authentication.supported_types format
      if (data.authentication_type) {
        backendData.authentication = {
          supported_types: [data.authentication_type],
          required: true
        };
        // Remove the frontend field
        delete (backendData as { authentication_type?: unknown }).authentication_type;
      }
      
      const response = await this.dataSource.put<Product>(`/api/v1/products/${id}`, backendData);
      return response.data;
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  }

  async deleteProduct(id: string): Promise<void> {
    try {
      await this.dataSource.delete(`/api/v1/products/${id}`);
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  }

  async archiveProduct(id: string): Promise<Product> {
    return this.updateProduct(id, { status: 'archived' });
  }

  async publishProduct(id: string): Promise<Product> {
    return this.updateProduct(id, { status: 'published' });
  }

  async duplicateProduct(id: string): Promise<Product> {
    try {
      const response = await this.dataSource.post<Product>(`/api/v1/products/${id}/duplicate`);
      return response.data;
    } catch (error) {
      console.error(`Error duplicating product ${id}:`, error);
      throw error;
    }
  }
}

export const productsService = new ProductsService();
